import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { 
  AlertTriangle, Star, Clock, BookOpen, Code, 
  ExternalLink, ChevronDown, ChevronUp, Loader,
  Sun, Moon, Calendar as CalendarIcon,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { useTheme } from 'next-themes';

// Stats Widget Component
const StatsWidget = ({ questions }) => {
  const stats = {
    easy: 0,
    medium: 0,
    hard: 0,
    total: 0
  };

  questions.forEach(topic => {
    topic.questions.forEach(q => {
      stats[q.level.toLowerCase()]++;
      stats.total++;
    });
  });

  return (
    <div className="flex flex-col gap-4">
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <div className="text-xl font-medium text-green-500">{stats.easy}</div>
                <div className="text-sm text-muted-foreground">Easy</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-medium text-yellow-500">{stats.medium}</div>
                <div className="text-sm text-muted-foreground">Medium</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-medium text-red-500">{stats.hard}</div>
                <div className="text-sm text-muted-foreground">Hard</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Calendar Component
const DSACalendar = ({ questions }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [animation, setAnimation] = useState('');

  const goToPreviousMonth = () => {
    setAnimation('slide-right');
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setTimeout(() => setAnimation(''), 300);
  };

  const goToNextMonth = () => {
    setAnimation('slide-left');
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setTimeout(() => setAnimation(''), 300);
  };

  useEffect(() => {
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startDay = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    const data = [];

    // Previous month days
    const prevMonthDays = startDay;
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const prevMonthLastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const date = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), prevMonthLastDay - i);
      data.push({
        date,
        isCurrentMonth: false,
        count: getQuestionsForDate(date, questions).length
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      data.push({
        date,
        isCurrentMonth: true,
        count: getQuestionsForDate(date, questions).length
      });
    }

    // Next month days
    const remainingDays = 42 - data.length; // 6 weeks
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i);
      data.push({
        date,
        isCurrentMonth: false,
        count: getQuestionsForDate(date, questions).length
      });
    }

    setCalendarData(data);
  }, [currentDate, questions]);

  const getQuestionsForDate = (date, questions) => {
    return questions.flatMap(topic => 
      topic.questions.filter(q => {
        const qDate = new Date(q.timestamp);
        return qDate.toDateString() === date.toDateString();
      })
    );
  };

  const getDateColor = (count, isCurrentMonth) => {
    const baseColor = isCurrentMonth ? '' : 'opacity-40 ';
    if (count === 0) return baseColor + 'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100';
    if (count === 1) return baseColor + 'bg-yellow-100 dark:bg-yellow-900 text-yellow-900 dark:text-yellow-100';
    return baseColor + 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100';
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="p-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-foreground flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            <span>
              {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </span>
          </CardTitle>
          <div className="flex items-center gap-1">
            <button 
              onClick={goToPreviousMonth}
              className="p-1 rounded-lg hover:bg-muted transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-2 py-0.5 text-xs rounded-lg hover:bg-muted transition-colors"
            >
              Today
            </button>
            <button 
              onClick={goToNextMonth}
              className="p-1 rounded-lg hover:bg-muted transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2">
        <div className={`grid grid-cols-7 gap-1 ${animation}`}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground">
              {day}
            </div>
          ))}
          {calendarData.map((day, index) => (
            <div
              key={index}
              className={`aspect-square rounded-md flex items-center justify-center text-xs font-medium
                ${getDateColor(day.count, day.isCurrentMonth)}
                transform hover:scale-105 transition-all duration-300
                ${day.date.toDateString() === new Date().toDateString() ? 
                  'ring-1 ring-primary' : ''}`}
            >
              <div className="relative">
                {day.date.getDate()}
                {day.count > 0 && (
                  <span className="absolute -top-1 -right-1 text-[10px] font-bold">
                    {day.count}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Question List Component
const QuestionList = ({ questions, expandedTopic, setExpandedTopic }) => {
  const getLevelColor = (level) => {
    const colors = {
      easy: 'text-green-500 dark:text-green-400',
      medium: 'text-yellow-500 dark:text-yellow-400',
      hard: 'text-red-500 dark:text-red-400'
    };
    return colors[level?.toLowerCase()] || 'text-gray-500 dark:text-gray-400';
  };

  return (
    <div className="grid gap-6">
      {questions.map((topicGroup, index) => (
        <Card 
          key={index} 
          className="bg-card border-border hover:border-primary/50 transition-all duration-300"
        >
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setExpandedTopic(expandedTopic === index ? null : index)}
          >
            <CardTitle className="text-xl text-foreground flex items-center justify-between">
              <span className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                {topicGroup.topic}
                <span className="text-sm text-muted-foreground">
                  ({topicGroup.questions.length})
                </span>
              </span>
              {expandedTopic === index ? 
                <ChevronUp className="w-5 h-5" /> : 
                <ChevronDown className="w-5 h-5" />
              }
            </CardTitle>
          </CardHeader>

          {expandedTopic === index && (
            <CardContent className="animate-slideDown">
              <div className="grid gap-4">
                {topicGroup.questions.map((question, qIndex) => (
                  <div 
                    key={qIndex}
                    className="bg-muted rounded-lg p-4 hover:bg-muted/80 transition-all duration-300
                      transform hover:-translate-y-1"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <h3 className="text-lg text-foreground font-medium flex items-center gap-2">
                          {question.important && (
                            <Star className="w-5 h-5 text-yellow-400 animate-pulse" />
                          )}
                          {question.question}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                          <span className={`flex items-center gap-1 ${getLevelColor(question.level)}`}>
                            <AlertTriangle className="w-4 h-4" />
                            {question.level}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(question.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <a 
                          href={question.leetcodelink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 
                            dark:hover:bg-blue-700 rounded-lg transition-colors duration-300"
                        >
                          <ExternalLink className="w-4 h-4 text-white" />
                        </a>
                        <a 
                          href={question.submissionlink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-green-500 hover:bg-green-600 dark:bg-green-600 
                            dark:hover:bg-green-700 rounded-lg transition-colors duration-300"
                        >
                          <Code className="w-4 h-4 text-white" />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
};

// Main DSAPlatform Component
const DSAPlatform = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [error, setError] = useState(null);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const database = getDatabase();
    const questionsRef = ref(database, 'dsa_questions');

    const unsubscribe = onValue(questionsRef, (snapshot) => {
      try {
        const data = snapshot.val();
        if (data) {
          const formattedQuestions = transformFirebaseData(data);
          setQuestions(formattedQuestions);
        }
        setLoading(false);
      } catch (err) {
        setError('Error loading questions');
        setLoading(false);
      }
    }, (error) => {
      setError('Error connecting to database');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const transformFirebaseData = (data) => {
    const groupedQuestions = Object.values(data).reduce((acc, question) => {
      const { topic, ...questionData } = question;
      if (!acc[topic]) {
        acc[topic] = { topic, questions: [] };
      }
      acc[topic].questions.push({
        ...questionData,
        timestamp: questionData.timestamp || new Date().toISOString()
      });
      return acc;
    }, {});

    Object.values(groupedQuestions).forEach(topicGroup => {
      topicGroup.questions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    });

    return Object.values(groupedQuestions);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 text-primary animate-spin" />
          <p className="text-foreground">Loading your DSA questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-destructive text-destructive-foreground p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground flex items-center gap-3">
            <Code className="w-8 h-8 md:w-10 md:h-10" />
            DSA Question Tracker
          </h1>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
          >
            {theme === 'dark' ? 
              <Sun className="w-5 h-5 text-primary" /> : 
              <Moon className="w-5 h-5 text-primary" />
            }
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr,250px] gap-6">
          <div className="space-y-6">
            {questions.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center text-muted-foreground">
                  No questions added yet. Send questions from Telegram to get started!
                </CardContent>
              </Card>
            ) : (
              <QuestionList 
                questions={questions}
                expandedTopic={expandedTopic}
                setExpandedTopic={setExpandedTopic}
              />
            )}
          </div>

          <div>
            <div className="space-y-4 sticky top-4">
              <StatsWidget questions={questions} />
              <DSACalendar questions={questions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DSAPlatform;